import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Loader2, Monitor, AlertTriangle } from 'lucide-react';
import logo from '../assets/images/logo.png';

const Login = () => {
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(false);
  const [skipAutoRedirect, setSkipAutoRedirect] = useState(false);

  // Check if user was redirected due to session expiry (logged in on another device)
  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('session_expired');
    if (sessionExpired === 'true') {
      setSessionExpiredMessage(true);
      sessionStorage.removeItem('session_expired');
      // Auto-hide after 10 seconds
      setTimeout(() => setSessionExpiredMessage(false), 10000);
    }
  }, []);

  // Redirect if already authenticated (but not if we need to change password)
  useEffect(() => {
    if (isAuthenticated && !authLoading && !skipAutoRedirect) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location, skipAutoRedirect]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    const result = await login({
      email: formData.email,
      password: formData.password
    });
    
    setLoading(false);
    
    if (result.success) {
      // Check if user was logged out from other devices
      const loggedOutDevices = result.data?.data?.logged_out_devices || 0;
      const mustChangePassword = result.data?.data?.must_change_password || false;
      
      // If user must change password (e.g., guest checkout user), redirect to change password
      if (mustChangePassword) {
        // Prevent the useEffect from auto-redirecting to dashboard
        setSkipAutoRedirect(true);
        
        toast.info({
          title: 'Password Change Required',
          description: 'Please change your password before continuing.',
        });
        navigate('/change-password', { 
          replace: true,
          state: { mustChangePassword: true, from: location.state?.from?.pathname || '/dashboard' }
        });
        return;
      }
      
      let description = 'Login successful. Redirecting to dashboard...';
      if (loggedOutDevices > 0) {
        description = `Login successful. You have been logged out from ${loggedOutDevices} other device${loggedOutDevices > 1 ? 's' : ''}.`;
      }
      
      toast.success({
        title: 'Welcome back!',
        description: description
      });
      
      // Navigate to dashboard or intended destination
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      // Handle specific error codes with appropriate messages
      if (result.code === 'INVALID_CREDENTIALS') {
        toast.error({
          title: 'Invalid Credentials',
          description: 'The email or password you entered is incorrect. Please try again.',
        });
      } else {
        toast.error({
          title: 'Login Failed',
          description: result.error || 'Something went wrong. Please try again.',
        });
      }
    }
  };

  // Show loading spinner while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Session Expired Alert - shown when user is logged out from another device */}
        {sessionExpiredMessage && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-red-800 font-medium">Session Expired</p>
              <p className="text-xs text-red-700 mt-1">
                You have been logged out because your account was signed in on another device. Please log in again to continue.
              </p>
            </div>
            <button 
              onClick={() => setSessionExpiredMessage(false)}
              className="flex-shrink-0 text-red-400 hover:text-red-600"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-full p-3 shadow-lg">
              <img 
                src={logo} 
                alt="Climb Capital Logo" 
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
          <p className="text-gray-600">Sign in to continue to Climb Capital</p>
        </div>

        {/* Single Device Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Monitor className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-amber-800 font-medium">Single Device Login</p>
            <p className="text-xs text-amber-700 mt-1">
              For your security, logging in here will automatically log you out from any other device where you're currently signed in.
            </p>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full border-2 rounded-lg px-4 py-3 focus:outline-none transition-colors ${
                  errors.email 
                    ? 'border-red-400 focus:border-red-500' 
                    : 'border-gray-200 focus:border-yellow-400'
                }`}
                placeholder="Enter your email"
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full border-2 rounded-lg px-4 py-3 pr-12 focus:outline-none transition-colors ${
                    errors.password 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-gray-200 focus:border-yellow-400'
                  }`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">{errors.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="mr-2 w-4 h-4 text-yellow-400 border-gray-300 rounded focus:ring-yellow-400" 
                />
                <span className="text-sm text-gray-600">Remember me</span>
              </label>
              <Link 
                to="/forgot-password" 
                className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-blue-600 font-bold hover:text-blue-800 hover:underline"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>
            By signing in, you agree to our{' '}
            <Link to="/terms-page" className="text-blue-600 hover:underline">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy-page" className="text-blue-600 hover:underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
